/* eslint-disable node/prefer-global/process */
import type { Dictionary, DictionaryType } from '@autobid/strapi-integration/typescript/webapi/Dictionary'
import type { QueryClient, QueryKey, UseQueryOptions } from '@tanstack/vue-query'
import { getError } from '@autobid/strapi-integration/utils/getError'
import { useQuery, useQueryClient } from '@tanstack/vue-query'

export type DictionaryOptions = UseQueryOptions<
  Record<string, Dictionary> | null,
  unknown
> & { type?: DictionaryType }

export function useDictionary(options?: DictionaryOptions) {
  const { locale } = useI18n()
  const { fetchDictionary } = useFetchDictionary()
  const queryClient = useQueryClient()

  const { data: dictionary, ...rest } = useQuery(
    ['dictionary', locale.value, options?.type ?? 'full'] as QueryKey,
    async (): Promise<Record<string, Dictionary> | null> => {
      if (process.client) {
        const alreadySavedFullDictionary = queryClient.getQueryData<
          Record<string, Dictionary>
        >(['dictionary', locale.value, 'full'])

        if (alreadySavedFullDictionary) {
          return alreadySavedFullDictionary
        }
      }

      const resp = await fetchDictionary(locale.value, options?.type)

      return resp
    },
    {
      refetchOnWindowFocus: false,
      ...(options ?? {}),
    },
  )

  return { dictionary, ...rest }
}

export type UseDictionary = ReturnType<typeof useDictionary>
export type DictionaryValue = UseDictionary['dictionary']['value']

function getDictionaryQueryKey(locale: string, type: DictionaryType) {
  return ['dictionary', locale, type]
}

function getSavedDictionary(queryClient: QueryClient, type: DictionaryType, locale: string): DictionaryValue | undefined {
  const currentQueryKey = getDictionaryQueryKey(locale, type)
  const fullQueryKey = getDictionaryQueryKey(locale, 'full')
  if (type !== 'full') {
    return (
      queryClient.getQueryData<DictionaryValue>(currentQueryKey)
      || (queryClient.getQueryData<DictionaryValue>(fullQueryKey))
    )
  }
  return queryClient.getQueryData<DictionaryValue>(currentQueryKey)
}

export function useFetchDictionary() {
  const { $trpc } = useNuxtApp()

  const fetchDictionary = async (lang: string, type?: DictionaryType) => {
    try {
      return await $trpc.webapi.getDictionary.query({ lang, type })
    }
    catch (error) {
      const { message, code, handled } = getError(error)

      if (!handled) {
        throw createError({ statusCode: code, statusMessage: message })
      }

      return null
    }
  }

  return { fetchDictionary }
}

export function usePrefetchDictionary(type: DictionaryType = 'full') {
  const queryClient = useQueryClient()
  const { locale } = useI18n()
  const queryKey = ['dictionary', locale.value, type]
  const { fetchDictionary } = useFetchDictionary()

  return async () => {
    const alreadyCachedDictionary = getSavedDictionary(
      queryClient,
      type,
      locale.value,
    )
    if (alreadyCachedDictionary) {
      return alreadyCachedDictionary as DictionaryValue | undefined
    }

    const prefetch = () => {
      return queryClient.prefetchQuery({
        queryKey,
        queryFn: async () => await fetchDictionary(locale.value, type),
      })
    }

    await prefetch()

    return queryClient.getQueryData(queryKey) as DictionaryValue | undefined
  }
}
